import { fi, type Messages } from './fi.ts';
import { formatCents } from '../../shared/money.ts';

type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

export type MessageKey = Leaves<Messages>;

const dictionaries: Record<string, unknown> = { fi };
let current: unknown = fi;
export let locale = 'fi';
export let localeTag = 'fi-FI';

export function setLocale(next: string): void {
  if (dictionaries[next]) {
    current = dictionaries[next];
    locale = next;
    localeTag = next === 'fi' ? 'fi-FI' : next;
    document.documentElement.lang = next;
  }
}

function lookup(key: string): string | undefined {
  let node: unknown = current;
  for (const part of key.split('.')) {
    if (node && typeof node === 'object' && part in node) node = (node as Record<string, unknown>)[part];
    else return undefined;
  }
  return typeof node === 'string' ? node : undefined;
}

/** `{n|pelaaja|pelaajaa}` picks the singular when n is 1 (Finnish: 1 pelaaja, 2 pelaajaa). */
function applyPlurals(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\|([^|}]*)\|([^}]*)\}/g, (_, name: string, one: string, many: string) => (Number(vars[name]) === 1 ? one : many));
}

export function t(key: MessageKey, vars: Record<string, string | number> = {}): string {
  const template = applyPlurals(lookup(key) ?? key, vars);
  return template.replace(/\{(\w+)\}/g, (_, name: string) => (name in vars ? String(vars[name]) : `{${name}}`));
}

/** Translation for dynamic keys such as server error codes; falls back gracefully. */
export function tDynamic(key: string, vars: Record<string, string | number> = {}, fallback?: string): string {
  const raw = lookup(key);
  if (raw === undefined) return fallback ?? key;
  const template = applyPlurals(raw, vars);
  return template.replace(/\{(\w+)\}/g, (_, name: string) => (name in vars ? String(vars[name]) : `{${name}}`));
}

export function errorText(err: unknown): string {
  const code = (err as { code?: string })?.code ?? 'unknown';
  return tDynamic(`errors.${code}`, {}, t('errors.unknown', { code }));
}

// ---- Formatting -------------------------------------------------------------

export const money = (cents: number) => formatCents(cents, localeTag);

const chipFormat = () => new Intl.NumberFormat(localeTag, { maximumFractionDigits: 0 });
export const chips = (n: number) => chipFormat().format(n);

export function dateTime(ms: number): string {
  return new Intl.DateTimeFormat(localeTag, { weekday: 'short', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }).format(ms);
}

export function dateOnly(ms: number): string {
  return new Intl.DateTimeFormat(localeTag, { day: 'numeric', month: 'numeric', year: 'numeric' }).format(ms);
}

export function timeOnly(ms: number): string {
  return new Intl.DateTimeFormat(localeTag, { hour: '2-digit', minute: '2-digit' }).format(ms);
}

/** Compact time for event logs: "14.32" today, "eilen 14.32", "ti 14.32" this week, else "12.9. 14.32". */
export function smartTime(ms: number, now = Date.now()): string {
  const day = (x: number) => new Date(x).setHours(0, 0, 0, 0);
  const days = Math.round((day(now) - day(ms)) / 86400_000);
  const time = timeOnly(ms);
  if (days === 0) return time;
  if (days === 1) return `${t('time.yesterday')} ${time}`;
  if (days > 1 && days < 7) return `${new Intl.DateTimeFormat(localeTag, { weekday: 'short' }).format(ms)} ${time}`;
  return `${new Intl.DateTimeFormat(localeTag, { day: 'numeric', month: 'numeric' }).format(ms)} ${time}`;
}

/** "11 h 20 min", "4 min 10 s" – the two largest units. */
export function duration(ms: number): string {
  if (ms <= 0) return t('time.now');
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const parts: string[] = [];
  if (d) parts.push(t('time.days', { n: d }));
  if (h) parts.push(t('time.hours', { n: h }));
  if (m && parts.length < 2) parts.push(t('time.minutes', { n: m }));
  if (!d && !h && parts.length < 2 && (sec > 0 || parts.length === 0)) parts.push(t('time.seconds', { n: sec }));
  return parts.slice(0, 2).join(' ');
}

/** "5 minuuttia sitten" for something that already happened: never in the future, even if clocks differ. */
export function relativePast(ms: number, now = Date.now()): string {
  return relativeTime(Math.min(ms, now), now);
}

export function relativeTime(ms: number, now = Date.now()): string {
  const diff = ms - now;
  const rtf = new Intl.RelativeTimeFormat(localeTag, { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60_000) return rtf.format(Math.round(diff / 1000), 'second');
  if (abs < 3600_000) return rtf.format(Math.round(diff / 60_000), 'minute');
  if (abs < 86400_000) return rtf.format(Math.round(diff / 3600_000), 'hour');
  return rtf.format(Math.round(diff / 86400_000), 'day');
}
